
import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  IconButton,
  Select,
  Badge,
  // Divider,
  Switch,
  useColorMode,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faBell, faBellSlash } from '@fortawesome/free-solid-svg-icons';
import { priceAlertManager } from '../utils/priceAlerts';
import { currencySymbols } from '../utils/currencyConversion.utils';
import { formatNumber } from '../utils/number.utils';

const PriceAlerts = ({ currentPrice, notificationsEnabled }) => {
  const { colorMode } = useColorMode();
  const [alerts, setAlerts] = useState([]);
  const [newAlertPrice, setNewAlertPrice] = useState('');
  const [newAlertDirection, setNewAlertDirection] = useState('above');
  const [alertStatus, setAlertStatus] = useState(null); // null, 'error', 'success'
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    // Load initial alerts
    setAlerts(priceAlertManager.getAllAlerts());
    
    // Listen for storage changes (if alerts are modified in another tab)
    const handleStorageChange = () => {
      setAlerts(priceAlertManager.getAllAlerts());
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleAddAlert = () => {
    const price = parseFloat(newAlertPrice);
    
    if (isNaN(price) || price <= 0) {
      setAlertStatus('error');
      setAlertMessage('Please enter a valid price');
      setTimeout(() => {
        setAlertStatus(null);
        setAlertMessage('');
      }, 3000);
      return;
    }

    priceAlertManager.addAlert(price, newAlertDirection);
    setAlerts(priceAlertManager.getAllAlerts());
    setNewAlertPrice('');
    
    setAlertStatus('success');
    setAlertMessage(`Alert set for ${newAlertDirection} ${currencyFlag}${price.toLocaleString()}`);
    setTimeout(() => {
      setAlertStatus(null);
      setAlertMessage('');
    }, 3000);
  };

  const handleRemoveAlert = (alertId) => {
    priceAlertManager.removeAlert(alertId);
    setAlerts(priceAlertManager.getAllAlerts());
  };

  const handleToggleAlert = (alertId) => {
    priceAlertManager.toggleAlert(alertId);
    setAlerts(priceAlertManager.getAllAlerts());
  };

  const selectedCurrency = localStorage.getItem('selectedCurrency') || 'usd';
  const currencyFlag = currencySymbols[selectedCurrency.toLowerCase()] || currencySymbols.usd;

  return (
    <Box 
      p={4} 
      borderRadius="3px" 
      bg={colorMode === 'dark' ? 'primary.300' : 'white'}
    >
      <VStack align="stretch" spacing={4}>
        <HStack justify="space-between">
          <HStack>
            <FontAwesomeIcon icon={faBell} color="#f7931a" />
            <Text fontWeight="bold" color={colorMode === 'dark' ? 'gray.100' : 'gray.800'}>Price Alerts</Text>
          </HStack>
          <Badge 
            bg="orange.900" 
            color="orange.200" 
            borderWidth="1px" 
            borderColor="orange.500"
          >
            Current: {currencyFlag}{currentPrice ? formatNumber(currentPrice, 0) : '---'}
          </Badge>
        </HStack>

        {/* <Divider borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.300'} /> */}

        {alertStatus && (
          <Box 
            p={2} 
            bg={alertStatus === 'success' ? 'orange.900' : 'red.900'} 
            borderRadius="3px" 
            borderWidth="1px" 
            borderColor={alertStatus === 'success' ? 'orange.500' : 'red.500'}
          >
            <Text fontSize="xs" color={alertStatus === 'success' ? 'orange.200' : 'red.200'}>
              {alertStatus === 'success' ? '✓' : '✗'} {alertMessage}
            </Text>
          </Box>
        )}

        {/* Add new alert */}
        <HStack>
          <Input
            type="number"
            placeholder={`Target price`}
            value={newAlertPrice}
            onChange={(e) => setNewAlertPrice(e.target.value)}
            size="sm"
            flex="1"
            bg={colorMode === 'dark' ? 'primary.700' : 'gray.50'}
            borderColor={colorMode === 'dark' ? 'gray.700' : 'gray.300'}
            color={colorMode === 'dark' ? 'gray.100' : 'gray.800'}
            _placeholder={{ color: colorMode === 'dark' ? 'gray.500' : 'gray.500' }}
            _hover={{ borderColor: 'orange.500' }}
            _focus={{ borderColor: 'orange.500', boxShadow: '0 0 0 1px #f7931a' }}
          />
          <Select
            value={newAlertDirection}
            onChange={(e) => setNewAlertDirection(e.target.value)}
            size="sm"
            width="150px"
            bg={colorMode === 'dark' ? 'primary.700' : 'gray.50'}
            borderColor={colorMode === 'dark' ? 'gray.700' : 'gray.300'}
            color={colorMode === 'dark' ? 'gray.100' : 'gray.800'}
            _hover={{ borderColor: 'orange.500' }}
            _focus={{ borderColor: 'orange.500', boxShadow: '0 0 0 1px #f7931a' }}
          >
            <option value="above">Above</option>
            <option value="below">Below</option>
          </Select>
          <IconButton
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={handleAddAlert}
            size="sm"
            bg="transparent"
            color="orange.500"
            borderWidth="2px"
            borderColor="orange.500"
            _hover={{ bg: 'orange.500', color: 'white' }}
            transition="all 0.2s"
            aria-label="Add alert"
          />
        </HStack>

        {/* Alert list */}
        <VStack align="stretch" spacing={2} maxH="200px" overflowY="auto">
          {alerts.length === 0 ? (
            <Text fontSize="sm" color={colorMode === 'dark' ? 'gray.400' : 'gray.500'} textAlign="center" py={2}>
              No price alerts set
            </Text>
          ) : (
            alerts.map((alert) => (
              <HStack
                key={alert.id}
                p={2}
                bg={colorMode === 'dark' 
                  ? (alert.active ? 'primary.700' : 'primary.900')
                  : (alert.active ? 'gray.50' : 'gray.100')
                }
                borderRadius="3px"
                borderWidth="1px"
                borderColor={colorMode === 'dark' 
                  ? '#ffffff'
                  : '#121212'
                }
                opacity={alert.active ? 1 : 0.6}
              >
                <Switch
                  size="sm"
                  isChecked={alert.active}
                  onChange={() => handleToggleAlert(alert.id)}
                  colorScheme="orange"
                />
                <Box flex={1}>
                  <Text fontSize="sm" color={colorMode === 'dark' ? 'gray.100' : 'gray.600'}>
                    {alert.direction === 'above' ? '📈' : '📉'}{' '}
                    {currencyFlag}{alert.targetPrice.toLocaleString()}
                  </Text>
                  {alert.triggeredAt && (
                    <Text fontSize="xs" color={colorMode === 'dark' ? 'gray.100' : 'gray.600'}>
                      Triggered {new Date(alert.triggeredAt).toLocaleString()}
                    </Text>
                  )}
                </Box>
                <IconButton
                  icon={<FontAwesomeIcon icon={faTrash} />}
                  size="xs"
                  variant="ghost"
                  color={colorMode === 'dark' ? 'gray.400' : 'gray.600'}
                  _hover={{ color: 'red.500', bg: 'transparent' }}
                  onClick={() => handleRemoveAlert(alert.id)}
                  aria-label="Remove alert"
                />
              </HStack>
            ))
          )}
        </VStack>

        <Text fontSize="xs" letterSpacing={1} color={colorMode === 'dark' ? 'gray.400' : 'gray.600'}>
          {notificationsEnabled ? (
            <>
              <FontAwesomeIcon icon={faBell} color="#f7931a" style={{ display: 'inline', marginRight: '4px' }} /> 
              Active alerts will notify you once when triggered
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faBellSlash} color={colorMode === 'dark' ? 'gray.400' : 'gray.600'} style={{ display: 'inline', marginRight: '4px' }} /> 
              Enable notifications above first
            </>
          )}
        </Text>
      </VStack>
    </Box>
  );
};

export default PriceAlerts;